import { useEffect, useState } from 'react';
import axios from 'axios';

const CategorySelector = ({
  onSelectCategory,
  selectedCategory,
  categories: vendorCategories = [],
}) => {
  const [apiCategories, setApiCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [orderedCategories, setOrderedCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (vendorCategories.length === 0) return;

      setLoadingCategories(true);
      try {
        const { data } = await axios.get(
          `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products/categories`,
          {
            headers: {
              sessionToken: localStorage.getItem('sessionToken'),
              refreshToken: localStorage.getItem('refreshToken'),
            },
          }
        );
        setApiCategories(data);

        // Sync API categories with vendor categories
        syncCategories(data, vendorCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');

        // If API fails, still use the vendor categories
        setOrderedCategories(vendorCategories);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [vendorCategories]);

  // Sync API categories with vendor categories
  const syncCategories = (apiCats, vendorCats) => {
    // Extract "All" category if it exists
    const hasAllCategory = vendorCats.includes('All');
    const vendorCatsWithoutAll = vendorCats.filter((cat) => cat !== 'All');

    // Filter API categories to only include those in vendor categories
    const filteredApiCategories = apiCats
      .filter((apiCat) => vendorCatsWithoutAll.includes(apiCat.name))
      .map((cat) => cat.name);

    // Add any vendor categories that might not be in the API
    const missingCategories = vendorCatsWithoutAll.filter(
      (vendorCat) => !filteredApiCategories.includes(vendorCat)
    );

    // Create the final ordered list with "All" first if it exists
    const finalOrderedCategories = hasAllCategory
      ? ['All', ...filteredApiCategories, ...missingCategories]
      : [...filteredApiCategories, ...missingCategories];

    setOrderedCategories(finalOrderedCategories);
  };

  // If no categories provided, return empty
  if (vendorCategories.length === 0) {
    return null;
  }

  // Render category item
  const renderCategoryItem = (category) => {
    const isSelected = category === selectedCategory;
    const iconPath = category === 'All' ? '/Fruits.png' : `/${category}.png`;

    return (
      <div
        key={category}
        className={`flex-shrink-0 col-span-1 w-full cursor-pointer transition-all duration-200`}
        onClick={() => onSelectCategory(category)}
      >
        <div
          className={`flex flex-col items-center justify-center w-full h-14 rounded-lg ${
            isSelected
              ? 'bg-green-100 border-2 border-green-500'
              : 'bg-gray-50 border border-gray-100 hover:bg-gray-100'
          }`}
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <img
              src={iconPath || '/placeholder.svg'}
              alt={category}
              width={32}
              height={32}
              className="object-contain"
              onError={(e) => {
                // Fallback to a default icon if the image doesn't exist
                e.currentTarget.src = '/Dairy.png';
              }}
            />
          </div>
          <span
            className={`mt-1 text-xs font-medium text-center ${isSelected ? 'text-green-700' : 'text-gray-700'}`}
          >
            {category}
          </span>
        </div>
      </div>
    );
  };

  // Split categories into "All" and the rest
  const allCategory = orderedCategories.find((cat) => cat === 'All');
  const otherCategories = orderedCategories.filter((cat) => cat !== 'All');

  if (loadingCategories) {
    return (
      <div className="flex space-x-3 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-20 h-20 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex space-x-3 mb-2">
      {/* Fixed "All" category */}
      {/* {allCategory && <div className="flex-shrink-0">{renderCategoryItem(allCategory)}</div>} */}

      {/* Scrollable container for the rest of categories */}
      <div className="grid grid-cols-5 gap-2 w-full">
        {allCategory && renderCategoryItem(allCategory)}
        {otherCategories.map(renderCategoryItem)}
      </div>
    </div>
  );
};

export default CategorySelector;
